import React, { Suspense, useEffect, useMemo, useState } from 'react';
import { Canvas, useThree } from '@react-three/fiber';
import { useWorldStore } from '../state/worldStore';
import { MANNEQUINS, MANNEQUIN_KEYS, useBodyStore, mannequinRig, resolveBody, useMannequinList } from '../game/mannequins';
import AnimatedResident from './AnimatedResident';

// CharacterSelect — the arcade screen. Who are you tonight, and where are you going.
//
// Khari, 2026-08-03, on the first attempt: "Why did you place everything in
// boxes?… It doesn't look like a website, that is why I offered it to you. What
// you've made looks like boxes on a screen… We are making a GAME. come out of
// website mode."
//
// He was right, so the rules here are deliberately anti-web:
//   · NOTHING is a card. No panel borders, no rounded containers, no max-width
//     column. A game screen is one composed image, not a set of widgets.
//   · The character BLEEDS off the bottom edge at full height. Cropping a body
//     against the frame is what makes it read as a character rather than a
//     thumbnail of one.
//   · Depth instead of dividers: glow, shadow and scale separate things, not
//     boxes drawn around them.
//   · The selection cursor is a CURSOR — a bright frame that moves — not a
//     background colour change on a tile.
//
// ⚠ IT PICKS A BODY, NOT AN ARTIST (2026-11-01). Khari: "bring back the
// character/avatar selection screen, but replacing the artists there with
// generic manneuquins the user can choose from. We will start with a simple
// male/famle pair." It was the come-up's save-slot screen (your artists, a new
// life) and went dark with it on 2026-10-23, when the artists began living their
// own lives. Now it is where a visitor chooses the mannequin they walk the city
// in (game/mannequins), shown once per load to everyone, guests included, with
// the last choice ready under ENTER. Third person follows from having a body.
const DESTINATIONS = [
  { key: 'city', name: 'THE CITY', note: 'Where the come-up happens', open: true, at: [0.46, 0.54] },
  // Locked, not hidden — the map's job tonight is to promise the expansion.
  { key: 'coast', name: 'THE COAST', note: 'Not open yet', open: false, at: [0.16, 0.76] },
  { key: 'uptown', name: 'UPTOWN', note: 'Not open yet', open: false, at: [0.78, 0.24] },
];

const GOLD = '#ffb25e';

// THE STAGE (Khari, 2026-08-03: "if the screen grows, the model, nameCard and
// slots also pull towards the center, avoiding empty space between the map and
// the model").
//
// Pinning the artist to the viewport's left edge is right up to a point and
// wrong past it — on an ultrawide it strands her a metre of empty background
// from the map she is meant to stand beside. So the whole composition lives on
// a fixed-width stage that CENTRES once the window outgrows it. Inside the
// stage she is still hard left; the stage is what drifts inward. Body,
// nameplate, roster and map all share that edge, so nothing drifts
// independently of anything else.
const STAGE = 1600;
const stageEdge = `max(0px, calc((100% - ${STAGE}px) / 2))`;
const stageBox = { maxWidth: STAGE, width: '100%', margin: '0 auto' };

const shell = {
  position: 'fixed', inset: 0, zIndex: 90, pointerEvents: 'auto', overflow: 'hidden',
  // THE SITE'S OWN BACKGROUND (Khari, 2026-08-03: "get rid of this brown
  // background.. look at the main website and use it's background instead").
  // Same base and the same two corner washes the site uses — warm at top-left,
  // cool at top-right — so moving from the website into the world reads as one
  // place rather than two products.
  backgroundColor: '#0a0a0f',
  backgroundImage:
    'radial-gradient(1100px 560px at 10% -6%, rgba(255,123,0,.13), transparent 60%),'
    + 'radial-gradient(1000px 600px at 95% 2%, rgba(30,136,229,.1), transparent 58%)',
  color: '#ffe9c4', fontFamily: 'system-ui, sans-serif',
  // Column, not side-by-side: the roster owns the full width of the bottom
  // (Khari, 2026-08-03: "move the selection row to the bottom of the screen. the
  // map will be larger and fill the rest of the top-right area"). With twenty
  // artists a bottom strip fits them in two rows across the whole screen instead
  // of a narrow scrolling column, and the map finally gets the room to read as a
  // world rather than a legend.
  display: 'flex', flexDirection: 'column',
};

// A roster cell's picture: the figure itself (cut from its plate), else its
// silhouette filled the way the stand-in is.
function Thumb({ src, img, size }) {
  if (img) return <img src={img} alt="" width={size} height={size} style={{ width: size, height: size, objectFit: 'cover', objectPosition: 'top', borderRadius: 6, display: 'block' }} />;
  const mask = {
    WebkitMaskImage: `url("${src}")`, maskImage: `url("${src}")`,
    WebkitMaskSize: 'contain', maskSize: 'contain',
    WebkitMaskPosition: 'center', maskPosition: 'center',
    WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat',
  };
  return (
    <div style={{ width: size, height: size, borderRadius: 6, background: 'rgba(255,233,196,0.05)', display: 'grid', placeItems: 'center' }}>
      <div style={{ width: size * 0.86, height: size * 0.86, ...mask,
        background: 'linear-gradient(180deg, #ffe2b8 0%, #ffb25e 100%)' }} />
    </div>
  );
}

// WHERE SHE STANDS, AS A FRACTION OF THE VIEW (Khari, 2026-08-03: "when the
// screen is slim, the 3D avatar is pushed off screen. Assure she stays on the
// left. at a max, covering 50% of her to assure space for the map").
//
// The offset was a fixed -0.62 world units. Camera FOV is VERTICAL, so a narrow
// canvas has a narrow HORIZONTAL field — the same 0.62 metres that reads as
// "left of centre" on a wide monitor walks her clean off a slim one. Her
// position has to be a share of what is actually visible, not a constant.
//
// Clamped both ways: never so far left that much more than half of her is past
// the edge, and never so near the middle that she crowds the map.
function Placed({ rig, onReady }) {
  const width = useThree((st) => st.viewport.width);
  // 40% max coverage, not 50% — she sits a little closer to the map.
  const x = Math.max(-0.92, Math.min(-0.12, -width * 0.19));
  return (
    <AnimatedResident
      key={rig.rigged}
      meshUrl={rig.rigged}
      rigPose={rig.pose || null}
      clipMap={rig.clips || null}
      clipUrls={[rig.idle, rig.walk].filter(Boolean)}
      play="idle"
      height={1.8}
      position={[x, -0.92, 0]}
      rotationY={0}
      onReady={onReady}
    />
  );
}

// THE BODY. Offset DOWN by half its height so the model's middle sits at the
// origin — R3F's default camera looks at [0,0,0], and with the body's feet there
// the first build framed nothing but boots.
// THEIR STANDARD ANIMATION (Khari, 2026-08-03: "use their standard animation..
// we will add more later. like salutes, waves, etc."). The idle clip lives in a
// SEPARATE GLB from the rigged mesh — the roster convention is
// <name>_rigged / <name>_idle / <name>_walk — so passing no clipUrls left the
// body standing in its bind pose, which is the T-shape-adjacent stillness that
// makes a character look broken rather than waiting. Same call the street and
// the interiors make. `play` matches on a substring, so when a salute or a wave
// clip is added to a roster entry, selecting it here is one more url in this
// array and one word in `play`.
function Body({ rig, onReady }) {
  if (!rig?.rigged) return null;
  return (
    <Canvas
      camera={{ position: [0, 0.15, 4.1], fov: 32 }}
      // NOT BOXED INTO A CELL (Khari, 2026-08-03: "see how her arms are cut off?
      // don't box her into the cell, design it so she can overlay other
      // background content"). She lived inside the left grid column, which
      // clipped her at its edge and sheared both arms off — a character sliced
      // by an invisible container reads as a rendering fault, not a crop.
      //
      // So the body is a LAYER, not a column: it spans most of the viewport at
      // the back and the UI floats above it. This is also what makes a wave or a
      // salute possible later — an animation that throws an arm out needs
      // somewhere to throw it.
      // THE CUT WAS THE CANVAS, NOT THE STACKING (Khari, 2026-08-03: "we still
      // have cutoff on the character's hand near the map.. we may need to adjust
      // z-index"). A WebGL canvas clips hard at its own edge, so an outstretched
      // hand ended exactly where the element did. Widening it is the fix — it is
      // transparent and pointer-through, so reaching across costs nothing — and
      // it sits ABOVE the map plate so she occludes the street lines instead of
      // being drawn through by them. The beacons stay on top of her.
      style={{ position: 'absolute', left: stageEdge, top: 0, bottom: 0,
        width: `min(66vw, ${Math.round(STAGE * 0.62)}px)`,
        zIndex: 1, pointerEvents: 'none' }}
      dpr={[1, 1.5]}
    >
      <ambientLight intensity={0.85} />
      <directionalLight position={[2.5, 4, 3]} intensity={1.5} />
      <directionalLight position={[-3, 2, -2]} intensity={0.5} color="#ffb25e" />
      <Suspense fallback={null}>
        <Placed rig={rig} onReady={onReady} />
      </Suspense>
    </Canvas>
  );
}


// HudMap — the city as a HUD overlay: streets as thin lines, edges dissolved.
//
// Khari, 2026-08-03: "please draw an svg line map (streets, etc represented by
// simple lines) to accompany the 3 destinations.. fade the edges so it looks
// like a HUD map."
//
// preserveAspectRatio="none" on purpose: the destination beacons are positioned
// in PERCENTAGES over this, so the drawing must stretch exactly as its box does
// or the pins drift off their streets. A little shear on a schematic reads as
// projection; a pin in the wrong district does not.
//
// The fade is a radial MASK, not a scrim laid on top — a scrim would wash the
// lines toward the middle too, and the point is that the network runs out of
// knowledge at the edges, the way a scanned map does.
function HudMap() {
  const V = [8, 18, 27, 34, 46, 58, 66, 76, 88];
  const H = [14, 24, 33, 42, 54, 63, 72, 84];
  return (
    <svg viewBox="0 0 100 100" preserveAspectRatio="none"
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
      <defs>
        <radialGradient id="hudFade" cx="50%" cy="50%" r="52%">
          <stop offset="0%" stopColor="#fff" stopOpacity="1" />
          <stop offset="62%" stopColor="#fff" stopOpacity="0.85" />
          <stop offset="88%" stopColor="#fff" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#fff" stopOpacity="0" />
        </radialGradient>
        <mask id="hudMask"><rect width="100" height="100" fill="url(#hudFade)" /></mask>
      </defs>
      <g mask="url(#hudMask)" fill="none" stroke="#7dd3ff" strokeLinecap="round">
        <g strokeWidth="0.22" opacity="0.30">
          {V.map((x) => <line key={`v${x}`} x1={x} y1="4" x2={x + 3} y2="96" />)}
          {H.map((y) => <line key={`h${y}`} x1="3" y1={y} x2="97" y2={y - 2} />)}
        </g>
        <g strokeWidth="0.5" opacity="0.55">
          <path d="M2 70 L38 58 L62 46 L98 30" />
          <path d="M46 2 L44 40 L49 66 L44 98" />
        </g>
        <g strokeWidth="0.4" opacity="0.42">
          <path d="M-2 82 C 12 74, 22 86, 34 80 C 44 75, 52 84, 62 79" />
          <path d="M-2 89 C 14 81, 24 93, 36 87" opacity="0.6" />
        </g>
        <g strokeWidth="0.18" opacity="0.45">
          {[38, 42, 46, 50, 54].map((x) => <line key={`dv${x}`} x1={x} y1="44" x2={x + 1} y2="66" />)}
          {[46, 50, 54, 58, 62].map((y) => <line key={`dh${y}`} x1="36" y1={y} x2="58" y2={y - 1} />)}
        </g>
        <g strokeWidth="0.2" opacity="0.34">
          {[70, 76, 82, 88].map((x) => <line key={`uv${x}`} x1={x} y1="12" x2={x + 4} y2="36" />)}
          {[16, 24, 32].map((y) => <line key={`uh${y}`} x1="68" y1={y} x2="94" y2={y + 3} />)}
        </g>
      </g>
    </svg>
  );
}

// ALWAYS, EVEN FOR ONE ARTIST (Khari, 2026-08-03: "this screen will show even if
// users have 1 artist. because we will allow them to choose the zone they are
// going to enter (only the city for now)").
//
// The obvious "optimisation" is to skip a picker with one option in it. Do not:
// the screen is not only a roster, it is the DEPARTURE — you choose who you are
// AND where you are going, and the second half has more than one answer coming.
// Skipping it for a single-artist member would mean rebuilding it the day the
// second zone opens, and teaching everyone a flow that is about to change.
//

// ── THE PLACEHOLDER BODY ─────────────────────────────────────────────────────
//
// Khari, 2026-08-03, after two attempts at hand-drawn SVG: "examples.. i don't
// know if you can pull this off. If not, I can get png files."
//
// He is right that I cannot. Those references — performer silhouettes with hair,
// instruments, boots and real weight in the pose — are hundreds of hand-placed
// bezier points each, authored against a visible canvas. Written blind they come
// out as the stick figure he already rejected. Art belongs in an asset.
//
// So this takes FILES. Drop black-on-transparent PNG or SVG cut-outs into
// public/silhouettes/ and list them below. They are rendered as a CSS MASK
// rather than as images, which means the artwork supplies only the SHAPE and the
// colour comes from here — so a black PNG picks up the interface's own gold and
// its fade, and nothing has to be re-exported when the palette moves.
//
// Bottom-anchored so a figure stands on the same ground line the 3D model uses.
// Chosen by avatar id, so an artist keeps the same stand-in every visit rather
// than shuffling on each render.
//
// Empty list → the plain fallback below. Never a broken image.
function Mannequin({ src }) {
  if (src) {
    // FILL AND OVERLAP, LIKE THE MODEL (Khari, 2026-08-03: "the image needs to
    // fill and overlap if needed, like the model does.. right now it is shrinking
    // with the cell"). `contain` inside an inset box made the figure a picture in
    // a frame — it shrank whenever its container did. The 3D body is sized to the
    // STAGE and bleeds off the bottom; this now does the same: scaled by HEIGHT
    // past 100% so it overflows, anchored bottom-left so it stands where the
    // model stands and crops against the frame the same way.
    const mask = {
      WebkitMaskImage: `url("${src}")`, maskImage: `url("${src}")`,
      // `auto 112%` sized by HEIGHT alone, which on a tall narrow window made
      // the WIDTH enormous — she swelled across the map and cropped to a
      // shoulder. `contain` inside a box that is bounded on BOTH axes lets
      // whichever dimension is scarce stay in charge, so she fills the left
      // without ever reaching the city.
      // HEIGHT DRIVES IT, THE BOX CLIPS IT. `contain` fits BOTH axes, so on a
      // slim window the narrow width decided her size and she shrank into the
      // bottom of a tall empty box. Sizing to 100% HEIGHT means the viewport's
      // height sets her scale on every shape of screen, and anything too wide is
      // simply cropped by the frame — which is what the 3D body does too.
      WebkitMaskSize: 'auto 100%', maskSize: 'auto 100%',
      WebkitMaskPosition: 'center bottom', maskPosition: 'center bottom',
      WebkitMaskRepeat: 'no-repeat', maskRepeat: 'no-repeat',
    };
    return (
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
        <div style={{
          position: 'absolute', inset: 0, ...mask,
          background: 'linear-gradient(180deg, rgba(255,201,143,0.26) 0%, rgba(255,178,94,0.15) 52%, rgba(255,178,94,0.05) 100%)',
        }} />
      </div>
    );
  }

  // FALLBACK — a plain standing form, deliberately quiet. It exists so the stage
  // is never empty before the art lands; it is not trying to be the art.
  return (
    <svg viewBox="0 0 240 540" preserveAspectRatio="xMidYMax meet"
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
      <defs>
        <linearGradient id="mqFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#ffc98f" stopOpacity="0.16" />
          <stop offset="52%" stopColor="#ffb25e" stopOpacity="0.10" />
          <stop offset="100%" stopColor="#ffb25e" stopOpacity="0.04" />
        </linearGradient>
      </defs>
      <ellipse cx="120" cy="524" rx="72" ry="10" fill="#ffb25e" opacity="0.05" />
      <g fill="url(#mqFill)">
        <ellipse cx="120" cy="46" rx="30" ry="38" />
        <path d="M106 74 L134 74 L136 106 Q120 116 104 106 Z" />
        <path d="M104 98 C86 102 70 114 64 132 C56 158 56 190 62 216
                 C66 238 70 256 74 272 L166 272 C170 256 174 238 178 216
                 C184 190 184 158 176 132 C170 114 154 102 136 98 Z" />
        <path d="M74 266 L166 266 C170 292 168 318 160 342 L80 342
                 C72 318 70 292 74 266 Z" />
        <path d="M66 126 C52 140 46 168 44 200 C42 232 42 268 44 296
                 C45 312 48 324 52 332 C58 337 65 335 67 328
                 C64 300 62 268 64 236 C66 204 70 166 78 140 Z" />
        <path d="M174 126 C188 140 194 168 196 200 C198 232 198 268 196 296
                 C195 312 192 324 188 332 C182 337 175 335 173 328
                 C176 300 178 268 176 236 C174 204 170 166 162 140 Z" />
        <path d="M82 336 C78 368 80 404 84 436 C86 462 88 486 88 502
                 L114 502 C114 486 114 462 114 436 C116 404 116 368 114 336 Z" />
        <path d="M126 336 C124 368 124 404 126 436 C126 462 126 486 126 502
                 L152 502 C152 486 154 462 156 436 C160 404 162 368 158 336 Z" />
        <path d="M84 496 L116 496 C118 508 116 516 106 516 L80 516
                 C74 514 76 500 84 496 Z" />
        <path d="M124 496 L156 496 C164 500 166 514 160 516 L134 516
                 C124 516 122 508 124 496 Z" />
      </g>
    </svg>
  );
}

// Once per page load — a departure, not a menu you keep landing back on.
// `?select=0` skips it (the last body is kept), `?select=1` shows it again.
let shownThisLoad = false;

export default function CharacterSelect() {
  const open = useWorldStore((s) => s.charSelect);
  const setOpen = useWorldStore((s) => s.setCharSelect);
  const entered = useWorldStore((s) => s.entered);
  const setEntered = useWorldStore((s) => s.setEntered);
  const body = useBodyStore((s) => s.body);
  const setBody = useBodyStore((s) => s.setBody);
  const [picked, setPicked] = useState(resolveBody(body) || MANNEQUIN_KEYS[0]);
  const [dest, setDest] = useState('city');
  const [ready, setReady] = useState('');

  // OPEN AT LOAD, UNDER THE DOOR. The Loader (z 100) covers this screen (z 95)
  // until its Enter, then fades to reveal it. Nothing here lowers `entered`
  // (that would bring the Loader back); what must wait for a body — the fly-in
  // and the station — waits on `charSelect` instead (PlanetScene, Hud).
  useEffect(() => {
    let forced = null;
    try { forced = new URLSearchParams(window.location.search).get('select'); } catch { forced = null; }
    if (forced === '0') { shownThisLoad = true; return; }
    if (shownThisLoad && forced !== '1') return;
    shownThisLoad = true;
    setOpen(true);
  }, [setOpen]);

  const listV = useMannequinList((s) => s.v);   // the API's set arrived
  const hero = MANNEQUINS[picked] || null;
  // The stand-in goes when the body reports in — and after a few seconds regardless,
  // so a missed report never leaves a silhouette standing behind the figure.
  useEffect(() => { const t = setTimeout(() => setReady(picked), 6000); return () => clearTimeout(t); }, [picked]);
  const rig = useMemo(() => mannequinRig(picked), [picked, listV]); // eslint-disable-line react-hooks/exhaustive-deps
  const canEnter = !!hero;

  const enter = () => {
    if (!canEnter) return;
    setBody(picked);
    setOpen(false);
    setEntered(true);   // NOW the city may start: fly-in, station, the lot
  };

  // ← → to choose, Enter to go — a game screen answers the keyboard. Only once
  // the door has faded (`entered`), and not for its first moment, so the key
  // that dismissed the Loader cannot also leave this screen.
  useEffect(() => {
    if (!open || !entered) return undefined;
    let live = false;
    const arm = setTimeout(() => { live = true; }, 900);
    const onKey = (e) => {
      if (!live || e.target?.closest?.('input, textarea, [contenteditable="true"]')) return;
      const i = MANNEQUIN_KEYS.indexOf(picked);
      if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
        const n = MANNEQUIN_KEYS.length;
        setPicked(MANNEQUIN_KEYS[(i + (e.key === 'ArrowRight' ? 1 : n - 1)) % n]);
        e.preventDefault();
      } else if (e.key === 'Enter') {
        enter();
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => { clearTimeout(arm); window.removeEventListener('keydown', onKey); };
  }); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  return (
    <div style={{ ...shell, zIndex: 95 }}>
      {/* The body rides BEHIND everything, edge to edge. Its silhouette holds
          the same place until the model has loaded. */}
      <Body rig={rig} onReady={() => setReady(picked)} />
      {ready !== picked && hero && (
        <div style={{ position: 'absolute', left: stageEdge, bottom: 46,
          height: 'min(92vh, 940px)',
          width: `min(70vw, ${Math.round(STAGE * 0.44)}px)`,
          zIndex: 1, pointerEvents: 'none' }}>
          <Mannequin src={hero.silhouette} />
        </div>
      )}

      <div style={{
        font: '900 15px system-ui', letterSpacing: 8, textTransform: 'uppercase',
        color: GOLD, textShadow: '0 0 18px rgba(255,178,94,0.5)', textAlign: 'center',
        padding: '22px 0 0', position: 'relative', zIndex: 2, pointerEvents: 'none',
      }}>
        Choose your body
      </div>

      {/* ── TOP: the body's half of the screen on the left, the world on the right ── */}
      <div style={{ ...stageBox, flex: '1 1 auto', position: 'relative', display: 'grid',
        gridTemplateColumns: 'minmax(240px, 34%) 1fr', minHeight: 0 }}>
        <div />

        {/* THE WORLD — big, borderless, its own half of the screen. */}
        <div style={{ position: 'relative', zIndex: 0, minWidth: 0 }}>
          <div style={{
            position: 'absolute', inset: 0,
            background: 'radial-gradient(52% 54% at 50% 48%, rgba(125,211,255,0.13) 0%, rgba(125,211,255,0.03) 46%, rgba(0,0,0,0) 72%)',
          }} />
          <HudMap />
          {/* THE MARKERS ARE THE DOORS (Khari, 2026-08-03: "the entrypoints will
              be the destination markers themselves"). The two unbuilt zones stay
              on the map, dimmed and labelled, to promise the expansion. */}
          {DESTINATIONS.map((d) => {
            const live = d.open && canEnter;
            return (
              <button
                key={d.key}
                onClick={() => { if (live) enter(); }}
                onMouseEnter={() => d.open && setDest(d.key)}
                disabled={!live}
                title={d.open ? `Enter ${d.name}` : `${d.name} — not open yet`}
                style={{
                  position: 'absolute', zIndex: 2,
                  left: `${d.at[0] * 100}%`, top: `${d.at[1] * 100}%`,
                  transform: `translate(-50%,-50%) scale(${dest === d.key && d.open ? 1.06 : 1})`,
                  background: 'none', border: 'none', padding: 8,
                  cursor: live ? 'pointer' : 'default', color: '#e9f6ff',
                  display: 'grid', placeItems: 'center', gap: 7,
                  opacity: d.open ? 1 : 0.38, transition: 'transform .15s',
                }}
              >
                <span style={{
                  width: d.open ? 20 : 11, height: d.open ? 20 : 11, borderRadius: '50%',
                  background: d.open ? '#9fe0ff' : 'rgba(233,246,255,0.45)',
                  boxShadow: d.open
                    ? '0 0 0 6px rgba(125,211,255,0.16), 0 0 34px 10px rgba(125,211,255,0.5)'
                    : 'none',
                }} />
                <span style={{
                  font: `${d.open ? 900 : 700} ${d.open ? 15 : 11}px system-ui`,
                  letterSpacing: d.open ? 3.5 : 2, whiteSpace: 'nowrap',
                  textShadow: '0 2px 12px rgba(0,0,0,0.95)',
                }}>
                  {d.name}
                </span>
                {d.open ? (
                  // A FILLED PILL (Khari, 2026-08-03): the one control that commits you.
                  <span style={{
                    font: '900 13px system-ui', letterSpacing: 3.5, whiteSpace: 'nowrap',
                    padding: '9px 20px', borderRadius: 999,
                    background: 'linear-gradient(180deg,#ffd08a,#ff9b3d)', color: '#1c1206',
                    boxShadow: '0 6px 22px rgba(255,155,61,0.55), 0 0 42px rgba(255,178,94,0.45)',
                  }}>
                    ENTER ▸
                  </span>
                ) : (
                  <span style={{ font: '700 9.5px system-ui', letterSpacing: 2.2, opacity: 0.8, whiteSpace: 'nowrap' }}>
                    COMING SOON
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── BOTTOM: who you will be, then the bodies to choose from ─────────── */}
      <div style={{
        position: 'relative', zIndex: 3, flexShrink: 0,
        padding: '12px 26px calc(max(12px, env(safe-area-inset-bottom)) + 20px)',
        background: 'linear-gradient(180deg, rgba(10,10,15,0) 0%, rgba(10,10,15,0.72) 22%, rgba(10,10,15,0.94) 100%)',
      }}>
        <div style={{ ...stageBox, display: 'flex', alignItems: 'flex-start', gap: 18, flexWrap: 'wrap' }}>
          {/* A FIXED width (Khari, 2026-08-04), so the slots never shift when the pick changes. */}
          {hero && (
            <div style={{ flexShrink: 0, width: 'min(360px, 100%)', overflow: 'hidden', paddingTop: 6 }}>
              <div style={{
                font: '900 clamp(22px, 3.2vw, 38px)/1.02 system-ui', letterSpacing: -0.5, color: '#fff4e2',
                textShadow: '0 0 22px rgba(255,178,94,0.45), 0 6px 18px rgba(0,0,0,0.9)', whiteSpace: 'nowrap',
              }}>
                {hero.label.toUpperCase()}
              </div>
              <div style={{ font: '800 11.5px system-ui', letterSpacing: 3, color: GOLD, marginTop: 5 }}>
                {hero.group.toUpperCase()} · MANNEQUIN · V SWITCHES VIEW
              </div>
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap', minWidth: 0 }}>
            {MANNEQUIN_KEYS.map((k) => {
              const m = MANNEQUINS[k];
              const on = k === picked;
              return (
                <button key={k} onClick={() => setPicked(k)} onDoubleClick={() => { setPicked(k); setBody(k); setOpen(false); setEntered(true); }}
                  aria-pressed={on} title={`${m.label} mannequin`}
                  style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                    display: 'grid', gap: 4, justifyItems: 'center', width: 74,
                    transform: on ? 'scale(1.06)' : 'none', transition: 'transform .12s' }}>
                  {/* The selection is a CURSOR — a bright frame that moves. */}
                  <div style={{ padding: 2, borderRadius: 8,
                    outline: on ? `3px solid ${GOLD}` : '1px solid rgba(255,255,255,0.14)',
                    outlineOffset: on ? 0 : 1,
                    boxShadow: on ? '0 0 22px rgba(255,178,94,0.65)' : 'none' }}>
                    <Thumb src={m.silhouette} img={m.thumb} size={64} />
                  </div>
                  <span style={{ font: `${on ? 800 : 600} 10px system-ui`, letterSpacing: 0.6,
                    color: on ? '#fff4e2' : 'rgba(255,233,196,0.62)' }}>
                    {m.label.toUpperCase()}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
